import React from 'react';
import { T, FONT_DISPLAY, FONT_BODY, FONT_MONO, eyebrow } from '../styleEditorial';
import { useInView } from './premiumKit';

/**
 * <EditorialImageBand /> — bande image éditoriale N&B pleine largeur.
 *
 * Photos Unsplash (libres de droit, aucune attribution requise mais recommandée).
 * Le filtre CSS grayscale + contrast donne un rendu N&B éditorial cohérent
 * avec la palette encre + papier crème + or.
 *
 * Props :
 *   - src      : URL Unsplash (sans paramètres optimisation, ils sont ajoutés)
 *   - alt      : description accessible
 *   - eyebrow  : ligne supérieure (ex : "§ Cabinet · Note photographique")
 *   - quote    : citation principale en serif italique
 *   - caption  : crédit/légende sous l'image (ex : "Bibliothèque du Sénat, Paris — vers 1920")
 *   - height   : hauteur cible (default 480)
 *   - reverse  : inverser texte/image (default false : image à droite)
 */
export default function EditorialImageBand({
  src,
  alt,
  eyebrow: eyebrowText,
  quote,
  caption,
  height = 480,
  reverse = false,
}) {
  const [ref, inView] = useInView({ threshold: 0.2 });

  // Optimisation URL Unsplash — srcset multi-tailles (évite de servir du 1600px à un mobile 375px)
  const baseParams = 'q=80&auto=format&fit=crop';
  const hasQuery = src.includes('?');
  const optimizedSrc = hasQuery ? src : `${src}?w=1200&${baseParams}`;
  const srcSet = hasQuery
    ? undefined
    : [640, 960, 1280, 1600].map(w => `${src}?w=${w}&${baseParams} ${w}w`).join(', ');

  return (
    <section ref={ref} style={{
      padding: '96px 32px',
      background: T.paper,
      borderTop: `1px solid ${T.ink}`,
      borderBottom: `1px solid ${T.ink}`,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '0.9fr 1.1fr',
        gap: '64px',
        alignItems: 'center',
      }} className="ed-imgband-grid">

        {/* Texte */}
        <div style={{
          order: reverse ? 2 : 1,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1)',
        }}>
          {eyebrowText && (
            <div style={{ ...eyebrow, marginBottom: '20px' }}>
              {eyebrowText}
            </div>
          )}
          {quote && (
            <blockquote style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 'clamp(22px, 2.6vw, 32px)',
              fontWeight: 400,
              lineHeight: 1.35,
              letterSpacing: '-0.015em',
              color: T.ink,
              margin: '0 0 24px 0',
              fontStyle: 'italic',
              position: 'relative',
              paddingLeft: '24px',
            }}>
              <span aria-hidden="true" style={{
                position: 'absolute',
                left: 0, top: '-12px',
                fontFamily: 'Georgia, serif',
                fontSize: '56px',
                color: T.gold,
                lineHeight: 1,
                fontStyle: 'italic',
                opacity: 0.6,
              }}>“</span>
              {quote}
            </blockquote>
          )}
          {caption && (
            <p style={{
              fontFamily: FONT_BODY,
              fontSize: '13px',
              color: T.muted,
              letterSpacing: '0.04em',
              margin: 0,
              fontStyle: 'italic',
              paddingLeft: '24px',
            }}>
              {`— ${caption}`}
            </p>
          )}
        </div>

        {/* Image */}
        <div style={{
          order: reverse ? 1 : 2,
          position: 'relative',
          height: `${height}px`,
          overflow: 'hidden',
          opacity: inView ? 1 : 0,
          transform: inView ? 'scale(1)' : 'scale(1.04)',
          transition: 'opacity 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.15s, transform 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
        }} className="ed-imgband-img">
          <img
            src={optimizedSrc}
            srcSet={srcSet}
            sizes="(max-width: 768px) 100vw, 50vw"
            alt={alt}
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'grayscale(1) contrast(1.08) brightness(0.96)',
              display: 'block',
            }}
          />
          {/* Coin or éditorial */}
          <div aria-hidden="true" style={{
            position: 'absolute',
            top: 0, right: 0,
            width: '52px', height: '52px',
            background: T.gold,
            clipPath: 'polygon(100% 0, 100% 100%, 0 0)',
          }} />
          {/* Numérotation cliché photo */}
          <div aria-hidden="true" style={{
            position: 'absolute',
            bottom: '14px', left: '16px',
            fontFamily: FONT_MONO,
            fontSize: '10.5px',
            // Fond opacifié 0,5 → 0,78 et texte plein : posée sur une photo, cette
            // légende tombait à 2,86:1 (audit 2026-08-15). Le fond laissait passer
            // l'image, dont la clarté varie d'un cliché à l'autre. À 0,78 le ratio
            // monte à 12,3:1 et ne dépend plus de la photo.
            color: 'rgba(250, 248, 243, 1)',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            background: 'rgba(10, 25, 41, 0.78)',
            padding: '4px 10px',
            backdropFilter: 'blur(4px)',
          }}>
            Cliché · Édition 2026
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .ed-imgband-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .ed-imgband-grid > div:first-child { order: 1 !important; }
          .ed-imgband-grid > div:last-child { order: 2 !important; }
          .ed-imgband-img { height: 320px !important; }
        }
      `}</style>
    </section>
  );
}
